import { CertificationRecord, Member, QueryEquipmentQuery, QueryEquipmentQueryVariables } from '__generated__/graphql';
import { EQUIPMENT_QUERY } from 'apollo/queries/equipment-queries';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import { difference, isNull } from 'lodash';
import { useCallback } from 'react';
import { getFullNameFromMember } from 'utils/stringUtils';
import { useCertificationRecordData } from './useCertificationRecordData';

export interface MemberMissingCerts {
  memberId: string;
  memberName: string;
  missingCerts: string[];
  equipmentId?: string;
}

export function useCertificationChecker() {
  const client = useGraphQLClient();
  const getCertificationRecords = useCertificationRecordData();

  const getMissingEquipmentCertifications = useCallback(
    async (memberIds: string[], equipmentIds: string[], members?: Member[]): Promise<MemberMissingCerts[]> => {
      if (equipmentIds.length === 0) {
        return [];
      }

      const certificationRecords: CertificationRecord[] = await getCertificationRecords(memberIds);

      const equipmentResult = await client.request<QueryEquipmentQuery, QueryEquipmentQueryVariables>({
        document: EQUIPMENT_QUERY,
        variables: { filter: { id: { contains: equipmentIds } } },
      });

      const equipmentCertMap = new Map<string, Set<string>>();
      const certificationNames = new Map<string, string>();

      equipmentResult?.equipment?.forEach((eq) => {
        const requiredCerts = new Set<string>();
        eq.certificationLinks?.forEach((link) => {
          if (
            link?.certificationId &&
            link.certification?.name &&
            isNull(link.deletedOn) &&
            isNull(link.certification.deletedOn)
          ) {
            requiredCerts.add(link.certificationId);
            certificationNames.set(link.certificationId, link.certification.name);
          }
        });
        equipmentCertMap.set(eq.id, requiredCerts);
      });

      const nameFromMembers = members
        ? new Map<string, string>(
            members.filter(Boolean).map((m) => [m.id, `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim()])
          )
        : undefined;

      const recordsByMember: Record<string, { memberId: string; memberName: string; certIds: Set<string> }> =
        memberIds.reduce(
          (acc, id) => {
            acc[id] = { memberId: id, memberName: nameFromMembers?.get(id) ?? '', certIds: new Set<string>() };
            return acc;
          },
          {} as Record<string, { memberId: string; memberName: string; certIds: Set<string> }>
        );

      for (const record of certificationRecords) {
        const memberId = record.memberId;

        if (!recordsByMember[memberId]) {
          recordsByMember[memberId] = {
            memberId,
            memberName: nameFromMembers?.get(memberId) ?? (record.member ? getFullNameFromMember(record.member) : ''),
            certIds: new Set<string>(),
          };
        } else if (!recordsByMember[memberId].memberName && record.member) {
          recordsByMember[memberId].memberName = getFullNameFromMember(record.member);
        }

        if (record.certificationId) {
          recordsByMember[memberId].certIds.add(record.certificationId);
        }
      }

      const result: MemberMissingCerts[] = [];

      for (const memberId of memberIds) {
        const member = recordsByMember[memberId];
        const ownedCerts = Array.from(member?.certIds ?? []);

        for (const equipmentId of equipmentIds) {
          const requiredCerts = Array.from(equipmentCertMap.get(equipmentId) ?? []);
          const missingIds = difference(requiredCerts, ownedCerts);
          const missingNames = missingIds.map((id) => certificationNames.get(id) ?? '');

          result.push({
            memberId: member.memberId,
            memberName: member?.memberName ?? '',
            missingCerts: missingNames,
            equipmentId,
          });
        }
      }

      return result;
    },
    [client, getCertificationRecords]
  );

  const getRequiredCertificationsFromEquipment = useCallback(
    async (equipmentIds: string[]): Promise<string[]> => {
      if (!equipmentIds.length) return [];

      try {
        const { equipment } = await client.request<QueryEquipmentQuery, QueryEquipmentQueryVariables>({
          document: EQUIPMENT_QUERY,
          variables: {
            filter: { id: { contains: equipmentIds } },
          },
        });

        const certIds =
          equipment?.flatMap((eq) =>
            eq.certificationLinks
              ?.filter((link) => isNull(link.deletedOn) && isNull(link.certification.deletedOn))
              .map((link) => link.certificationId)
          ) ?? [];

        return Array.from(new Set(certIds));
      } catch (error) {
        console.error('Failed to load certification IDs:', error);
        return [];
      }
    },
    [client]
  );

  return { getMissingEquipmentCertifications, getRequiredCertificationsFromEquipment };
}
